<template>
        <div class="min-h-screen bg-gray-50 font-sans">
                <!-- Header -->
                <header class="bg-white border-b border-gray-200 px-3 py-4">
                        <div class="w-full flex justify-between items-center flex-wrap gap-2">
                                <div class="flex items-center gap-4">
                                        <button
                                                type="button"
                                                class="inline-flex items-center gap-2 px-3 py-2 text-gray-500 bg-transparent border-none rounded-lg text-sm cursor-pointer transition-colors hover:bg-gray-100"
                                                @click="goBack"
                                        >
                                                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                                                </svg>
                                                Back to Hyperlinks
                                        </button>
                                </div>
                                <div>
                                        <h1 class="text-2xl font-semibold text-gray-900 m-0">Edit Hyperlink</h1>
                                        <p class="text-sm text-gray-500 mt-1 mb-0">Update hyperlink properties</p>
                                </div>
                                <button
                                        type="button"
                                        :disabled="isSaving || !hasChanges"
                                        class="inline-flex items-center gap-2 px-4 py-2 bg-orange-7 text-white border-none rounded-lg text-sm font-medium cursor-pointer transition-colors hover:bg-orange-6 disabled:opacity-50 disabled:cursor-not-allowed"
                                        @click="saveHyperlink"
                                >
                                        <svg v-if="isSaving" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        <svg v-else class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                                        </svg>
                                        {{ isSaving ? 'Saving...' : 'Save Changes' }}
                                </button>
                        </div>
                </header>

                <!-- Main Content -->
                <main class="w-full p-3">
                        <!-- Loading State -->
                        <div v-if="isLoading" class="flex justify-center items-center py-16 px-4">
                                <svg class="w-10 h-10 text-orange-7 animate-spin" fill="none" viewBox="0 0 24 24">
                                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                <span class="ml-3 text-gray-500 text-sm">Loading hyperlink...</span>
                        </div>

                        <!-- Edit Form -->
                        <div v-else class="max-w-3xl mx-auto">
                                <div class="bg-white rounded-xl shadow-sm overflow-hidden">
                                        <!-- Card Header -->
                                        <div class="px-6 py-4 border-b border-gray-200 bg-gray-50">
                                                <h2 class="text-lg font-semibold text-gray-900 m-0">Hyperlink Properties</h2>
                                        </div>
                                        <!-- Card Body -->
                                        <div class="p-6">
                                                <div class="grid gap-5">
                                                        <!-- Language -->
                                                        <div class="grid grid-cols-[10rem_1fr] items-center gap-4">
                                                                <label class="text-sm font-medium text-gray-700">Language *</label>
                                                                <select
                                                                        v-model="hyperlink.languageid"
                                                                        class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:border-orange-7"
                                                                >
                                                                        <option value="">Select a language</option>
                                                                        <option v-for="lang in languages" :key="lang.id" :value="lang.id">{{ lang.name }}</option>
                                                                </select>
                                                        </div>
                                                        <!-- Region -->
                                                        <div class="grid grid-cols-[10rem_1fr] items-center gap-4">
                                                                <label class="text-sm font-medium text-gray-700">Region *</label>
                                                                <select
                                                                        v-model="hyperlink.regionid"
                                                                        class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:border-orange-7"
                                                                >
                                                                        <option :value="null">Select a region</option>
                                                                        <option v-for="region in regions" :key="region.id" :value="region.id">{{ region.name }}</option>
                                                                </select>
                                                        </div>
                                                        <!-- PDP -->
                                                        <div class="grid grid-cols-[10rem_1fr] items-center gap-4">
                                                                <label class="text-sm font-medium text-gray-700">PDP</label>
                                                                <select
                                                                        v-model="hyperlink.pdpid"
                                                                        class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:border-orange-7"
                                                                >
                                                                        <option :value="null">None</option>
                                                                        <option v-for="p in pdps" :key="p.id" :value="p.id">{{ p.name }}</option>
                                                                </select>
                                                        </div>
                                                        <!-- Product Category -->
                                                        <div class="grid grid-cols-[10rem_1fr] items-center gap-4">
                                                                <label class="text-sm font-medium text-gray-700">Product Category</label>
                                                                <select
                                                                        v-model="hyperlink.productcategoryid"
                                                                        class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:border-orange-7"
                                                                >
                                                                        <option :value="null">None</option>
                                                                        <option v-for="cat in productCategories" :key="cat.id" :value="cat.id">{{ cat.name }}</option>
                                                                </select>
                                                        </div>
                                                        <!-- Tag -->
                                                        <div class="grid grid-cols-[10rem_1fr] items-center gap-4">
                                                                <label class="text-sm font-medium text-gray-700">Tag</label>
                                                                <input
                                                                        v-model="hyperlink.tag"
                                                                        type="text"
                                                                        placeholder="Enter tag (optional)"
                                                                        class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:border-orange-7"
                                                                >
                                                        </div>
                                                        <!-- URL -->
                                                        <div class="grid grid-cols-[10rem_1fr] items-center gap-4">
                                                                <label class="text-sm font-medium text-gray-700">URL</label>
                                                                <input
                                                                        v-model="hyperlink.url"
                                                                        type="text"
                                                                        placeholder="https://..."
                                                                        class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:border-orange-7"
                                                                >
                                                        </div>
                                                        <!-- Delegate -->
                                                        <div class="grid grid-cols-[10rem_1fr] items-center gap-4">
                                                                <label class="text-sm font-medium text-gray-700">Delegate</label>
                                                                <div class="flex items-center gap-3">
                                                                        <button
                                                                                type="button"
                                                                                class="relative w-11 h-6 rounded-full border-none cursor-pointer transition-colors"
                                                                                :class="hyperlink.delegate ? 'bg-orange-7' : 'bg-gray-300'"
                                                                                @click="hyperlink.delegate = hyperlink.delegate ? 0 : 1"
                                                                        >
                                                                                <span
                                                                                        class="absolute top-0.5 w-5 h-5 bg-white rounded-full transition-all shadow-sm"
                                                                                        :class="hyperlink.delegate ? 'left-[1.375rem]' : 'left-0.5'"
                                                                                ></span>
                                                                        </button>
                                                                        <span class="text-sm text-gray-500">{{ hyperlink.delegate ? 'Enabled' : 'Disabled' }}</span>
                                                                </div>
                                                        </div>
                                                </div>
                                        </div>
                                </div>
                        </div>
                </main>

                <!-- Toast Notification -->
                <div v-if="toastMessage" class="fixed bottom-6 right-6 z-50">
                        <div
                                class="flex items-center gap-3 px-5 py-4 rounded-lg shadow-lg"
                                :class="toastVariant === 'success' ? 'bg-emerald-600' : 'bg-red-600'"
                        >
                                <svg v-if="toastVariant === 'success'" class="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                        <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                                </svg>
                                <svg v-else class="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                        <path stroke-linecap="round" stroke-linejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <span class="text-sm font-medium text-white">{{ toastMessage }}</span>
                                <button
                                        type="button"
                                        class="ml-2 text-white opacity-70 bg-transparent border-none cursor-pointer hover:opacity-100"
                                        @click="hideToast"
                                >
                                        <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                </button>
                        </div>
                </div>
        </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch, Ref } from 'vue';
import { useRouter, useRoute, Router, RouteLocationNormalizedLoaded } from 'vue-router';
import { useAuth0 } from '@auth0/auth0-vue';
import HyperlinkService from 'services/HyperlinkService';
import LanguageService from 'services/LanguageService';
import RegionService from 'services/RegionService';
import PDPService from 'services/PDPService';
import ProductCategoryService from 'services/ProductCategoryService';
import type { HyperlinkModel, LanguageModel, RegionModel, PDPModel, ProductCategoryModel } from '@sosocio/creator/interfaces';

interface Auth0Interface {
        isAuthenticated: Ref<boolean>;
}

export default defineComponent({
        name: 'EditHyperlinksPage',
        setup() {
                const router: Router = useRouter();
                const route: RouteLocationNormalizedLoaded = useRoute();
                const auth0 = useAuth0() as unknown as Auth0Interface;

                const hyperlink: Ref<Partial<HyperlinkModel>> = ref({});
                const originalHyperlink: Ref<Partial<HyperlinkModel> | null> = ref(null);
                const languages: Ref<LanguageModel[]> = ref([]);
                const regions: Ref<RegionModel[]> = ref([]);
                const pdps: Ref<PDPModel[]> = ref([]);
                const productCategories: Ref<ProductCategoryModel[]> = ref([]);
                const isLoading: Ref<boolean> = ref(true);
                const isSaving: Ref<boolean> = ref(false);

                const toastMessage: Ref<string> = ref('');
                const toastVariant: Ref<string> = ref('success');
                let toastTimeout: ReturnType<typeof setTimeout> | null = null;

                const loggedIn = computed((): boolean => auth0.isAuthenticated.value);

                const hasChanges = computed((): boolean => {
                        if (!originalHyperlink.value) return false;
                        return (
                                hyperlink.value.languageid !== originalHyperlink.value.languageid ||
                                hyperlink.value.regionid !== originalHyperlink.value.regionid ||
                                hyperlink.value.pdpid !== originalHyperlink.value.pdpid ||
                                hyperlink.value.productcategoryid !== originalHyperlink.value.productcategoryid ||
                                hyperlink.value.tag !== originalHyperlink.value.tag ||
                                hyperlink.value.url !== originalHyperlink.value.url ||
                                hyperlink.value.delegate !== originalHyperlink.value.delegate
                        );
                });

                const showToast = (message: string, variant: string = 'success'): void => {
                        toastMessage.value = message;
                        toastVariant.value = variant;
                        if (toastTimeout) clearTimeout(toastTimeout);
                        toastTimeout = setTimeout(() => {
                                toastMessage.value = '';
                        }, 4000);
                };

                const hideToast = (): void => {
                        toastMessage.value = '';
                        if (toastTimeout) clearTimeout(toastTimeout);
                };

                const goBack = (): void => {
                        router.push('/hyperlink');
                };

                const loadData = async (): Promise<void> => {
                        if (!loggedIn.value) return;

                        isLoading.value = true;
                        try {
                                const hyperlinkId = Number(route.params.id);
                                const params = new URLSearchParams({ limit: '0' });
                                const [hyperlinkData, languageData, regionData, pdpData, categoryData] = await Promise.all([
                                        HyperlinkService.get(hyperlinkId),
                                        LanguageService.getAll(params),
                                        RegionService.getAll(params),
                                        PDPService.getAll(params),
                                        ProductCategoryService.getAll(params),
                                ]);
                                hyperlink.value = { ...hyperlinkData };
                                originalHyperlink.value = { ...hyperlinkData };
                                languages.value = languageData;
                                regions.value = regionData;
                                pdps.value = pdpData;
                                productCategories.value = categoryData;
                        } catch (error: unknown) {
                                const errorMessage = error instanceof Error ? error.message : 'Failed to load hyperlink';
                                showToast(errorMessage, 'error');
                        } finally {
                                isLoading.value = false;
                        }
                };

                const saveHyperlink = async (): Promise<void> => {
                        if (!hasChanges.value) return;

                        isSaving.value = true;
                        try {
                                await HyperlinkService.update(hyperlink.value.id!, { ...hyperlink.value });
                                originalHyperlink.value = { ...hyperlink.value };
                                showToast('Hyperlink saved successfully', 'success');
                        } catch (error: unknown) {
                                const errorMessage = error instanceof Error ? error.message : 'Failed to save hyperlink';
                                showToast(errorMessage, 'error');
                        } finally {
                                isSaving.value = false;
                        }
                };

                watch(loggedIn, async (isLoggedIn: boolean): Promise<void> => {
                        if (isLoggedIn) {
                                await loadData();
                        }
                }, { immediate: true });

                return {
                        hyperlink,
                        languages,
                        regions,
                        pdps,
                        productCategories,
                        isLoading,
                        isSaving,
                        hasChanges,
                        toastMessage,
                        toastVariant,
                        goBack,
                        saveHyperlink,
                        hideToast,
                };
        },
});
</script>
